

import React, { useState, useEffect } from 'react';
import { Edit, Trash, ChevronDown, ChevronUp, Save, X, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { updateProduct, archiveProduct } from '../utils/productApi';
import { getTaxonomyForFilters } from '../utils/taxonomyApi';
import { COLORS } from '../lib/styleGuide';

// Style constants
const FONTS = {
  heading: { fontFamily: 'Inter, system-ui, sans-serif' },
  body: { fontFamily: 'Inter, system-ui, sans-serif' }
};

export function ProductCard({ 
  product, 
  onDelete, 
  isExpanded, 
  onToggleExpand,
  className = '',
  onUpdate
}) {

  const navigate = useNavigate();
  
  // Taxonomy data state
  const [taxonomyData, setTaxonomyData] = useState({
    listingType: [],
    itemCategory: [],
    cuisine: [],
    courseType: [],
    dishType: [],
    dietaryTags: []
  });

  // Fetch taxonomy data on component mount
  useEffect(() => {
    const loadTaxonomyData = async () => {
      try {
        const data = await getTaxonomyForFilters();
        setTaxonomyData(data);
      } catch (error) {
        console.error('Error loading taxonomy data:', error);
      }
    };
    
    loadTaxonomyData();
  }, []);
  
        // Simple approach: just use the product's scheduled data directly
  const generateMissingScheduledDates = (productData) => {
    const existingScheduled = productData.inventory?.scheduled || [];
    const existingHistory = productData.inventory?.history || [];
    const templates = productData.inventory?.template || [];
    const policy = productData.inventory?.policy || {};
    
    // Get store timezone (default to America/New_York if not available)
    const storeTimezone = productData.storeTimezone || 'America/New_York';
    
    // Get current date in store's timezone
    const now = new Date();
    const storeDate = new Date(now.toLocaleString("en-US", {timeZone: storeTimezone}));
    const todayString = storeDate.toISOString().split('T')[0];
    
    // Determine start date for generation - use policy.start_date if it's in the future, otherwise start from today
    // Normalize storeDate to noon to avoid timezone issues
    const normalizedStoreDate = new Date(storeDate);
    normalizedStoreDate.setHours(12, 0, 0, 0);
    
    let generationStartDate;
    if (policy.start_date) {
      // Parse date string directly to avoid timezone issues
      const [year, month, day] = policy.start_date.split('-').map(Number);
      const policyStart = new Date(year, month - 1, day, 12, 0, 0, 0); // month is 0-indexed, set to noon
      if (policyStart > normalizedStoreDate) {
        generationStartDate = policyStart;
      } else {
        generationStartDate = new Date(normalizedStoreDate);
        generationStartDate.setDate(normalizedStoreDate.getDate() + 1); // Start from tomorrow
      }
    } else {
      generationStartDate = new Date(normalizedStoreDate);
      generationStartDate.setDate(normalizedStoreDate.getDate() + 1); // Start from tomorrow
    }
    generationStartDate.setHours(12, 0, 0, 0); // Ensure noon
    
    // Determine end date for generation - use policy.end_date or 28 days from start
    let generationEndDate = new Date(generationStartDate);
    generationEndDate.setDate(generationStartDate.getDate() + 28); // Default to 28 days ahead
    
    if (policy.end_date) {
      // Parse date string directly to avoid timezone issues
      const [year, month, day] = policy.end_date.split('-').map(Number);
      const policyEnd = new Date(year, month - 1, day, 12, 0, 0, 0); // month is 0-indexed, set to noon
      if (policyEnd < generationEndDate) {
        generationEndDate = policyEnd;
      }
    }
    
    // Separate past dates and future dates
    const pastDates = [];
    const futureDates = [];
    
    existingScheduled.forEach(scheduled => {
      if (scheduled.service_date < todayString) {
        // Move past dates to history
        pastDates.push({
          ...scheduled,
          migrated_at: new Date().toISOString()
        });
      } else {
        // Keep future dates in scheduled, but update them if they're template-sourced and outside date range
        // Parse date string directly to avoid timezone issues
        const [year, month, day] = scheduled.service_date.split('-').map(Number);
        const scheduledDate = new Date(year, month - 1, day, 12, 0, 0, 0);
        const isInRange = scheduledDate >= generationStartDate && scheduledDate <= generationEndDate;
        const isTemplateSource = scheduled.source === 'template';
        
        // If template-sourced date is outside date range, mark as disabled
        if (isTemplateSource && !isInRange) {
          futureDates.push({
            ...scheduled,
            enabled: false
          });
        } else {
          futureDates.push(scheduled);
        }
      }
    });
    
    // Update history with past dates (avoid duplicates)
    const updatedHistory = [...existingHistory];
    pastDates.forEach(pastDate => {
      const existingHistoryEntry = updatedHistory.find(h => h.service_date === pastDate.service_date);
      if (!existingHistoryEntry) {
        updatedHistory.push(pastDate);
      }
    });
    
    // Generate dates for the date range
    const generatedDates = [];
    const processedDateStrings = new Set(); // Track which dates we've already processed
    
    // First, create a map of existing future dates for quick lookup
    // Also recalculate and fix weekdays for all existing dates
    const existingDatesMap = new Map();
    futureDates.forEach(date => {
      // Always recalculate weekday from service_date to fix any incorrect stored values
      const [year, month, day] = date.service_date.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day, 12, 0, 0, 0);
      const correctWeekday = dateObj.getDay();
      
      // Update the date with the correct weekday
      const correctedDate = {
        ...date,
        weekday: correctWeekday
      };
      existingDatesMap.set(date.service_date, correctedDate);
    });
    
    // Generate dates from generationStartDate to generationEndDate
    // Normalize dates to noon to avoid timezone issues when calculating weekday
    const currentDate = new Date(generationStartDate);
    currentDate.setHours(12, 0, 0, 0); // Set to noon to avoid DST/timezone edge cases
    
    const endDate = new Date(generationEndDate);
    endDate.setHours(12, 0, 0, 0);
    
    while (currentDate <= endDate) {
      // Format date as YYYY-MM-DD without using toISOString (which can shift dates due to timezone)
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // getMonth() is 0-indexed
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      const weekday = currentDate.getDay(); // Should now be consistent since date is at noon
      
      // Find template for this weekday
      const template = templates.find(t => t.weekday === weekday);
      
      // Check if this date already exists
      const existingDate = existingDatesMap.get(dateString);
      
      if (existingDate) {
        // Date exists - update it if it's template-sourced, otherwise keep manual overrides
        if (existingDate.source === 'template' && template) {
          // Update existing template-sourced date to match current template
          // Use the weekday we calculated from the date loop, not the stored weekday
          generatedDates.push({
            ...existingDate,
            enabled: template.enabled,
            max_total: template.enabled ? (template.default_max_total || 0) : 0,
            available_total: template.enabled ? Math.max(0, (template.default_max_total || 0) - (existingDate.ordered_total || 0)) : 0,
            start_time_local: template.start_time_local || '08:00',
            end_time_local: template.end_time_local || '18:00',
            weekday: weekday // Always use the correctly calculated weekday from the date loop
          });
        } else {
          // Manual override (source !== 'template') - keep as is but fix weekday
          generatedDates.push({
            ...existingDate,
            weekday: weekday // Always use the correctly calculated weekday from the date loop
          });
        }
        processedDateStrings.add(dateString);
      } else {
        // Date doesn't exist - generate from template
        if (template && template.enabled) {
          // Generate new scheduled date from template
          const newScheduled = {
            service_date: dateString,
            weekday: weekday,
            source: 'template',
            enabled: template.enabled,
            max_total: template.default_max_total || 0,
            ordered_total: 0,
            available_total: template.default_max_total || 0,
            start_time_local: template.start_time_local || '08:00',
            end_time_local: template.end_time_local || '18:00'
          };
          generatedDates.push(newScheduled);
          processedDateStrings.add(dateString);
        } else if (template && !template.enabled) {
          // Template exists but is disabled - create disabled entry
          const newScheduled = {
            service_date: dateString,
            weekday: weekday,
            source: 'template',
            enabled: false,
            max_total: 0,
            ordered_total: 0,
            available_total: 0,
            start_time_local: template.start_time_local || '08:00',
            end_time_local: template.end_time_local || '18:00'
          };
          generatedDates.push(newScheduled);
          processedDateStrings.add(dateString);
        }
      }
      
      // Move to next day (keep at noon to avoid timezone shifts)
      currentDate.setDate(currentDate.getDate() + 1);
      currentDate.setHours(12, 0, 0, 0); // Re-normalize to noon
    }
    
    // Add existing future dates that are outside the generation range
    futureDates.forEach(existing => {
      // Parse date string directly and normalize to noon to avoid timezone issues
      const [year, month, day] = existing.service_date.split('-').map(Number);
      const existingDate = new Date(year, month - 1, day, 12, 0, 0, 0);
      const correctWeekday = existingDate.getDay(); // Always recalculate weekday
      
      if (existingDate > generationEndDate) {
        // Date is beyond end date but still in the future - keep it but mark disabled if template-sourced and fix weekday
        if (existing.source === 'template') {
          generatedDates.push({
            ...existing,
            enabled: false,
            weekday: correctWeekday // Fix the weekday
          });
        } else {
          generatedDates.push({
            ...existing,
            weekday: correctWeekday // Fix the weekday
          });
        }
      } else if (existingDate >= generationStartDate && existingDate <= generationEndDate && !processedDateStrings.has(existing.service_date)) {
        // This shouldn't happen, but if we missed a date in range, recalculate its weekday and add it
        generatedDates.push({
          ...existing,
          weekday: correctWeekday // Fix the weekday
        });
      }
    });
    
    // Sort by date
    generatedDates.sort((a, b) => a.service_date.localeCompare(b.service_date));
    
    return { scheduled: generatedDates, history: updatedHistory };
  };

  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedWeekdays, setExpandedWeekdays] = useState([]); // Collapse all weekdays by default
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [ingredientResults, setIngredientResults] = useState([]);
  const [isSearchingIngredients, setIsSearchingIngredients] = useState(false);
  const [editData, setEditData] = useState(() => {
    
    // Initialize with product data and auto-generate missing scheduled dates
    const initialData = {
      name: product.name || '',
      description: product.description || '',
      price: product.price || 0,
      stock: product.stock || 0,
      status: product.status || false,
      listingType: product.listingType || '',
      itemCategory: product.itemCategory || '',
      cuisine: product.cuisine || '',
      courseType: product.courseType || '',
      dishType: product.dishType || '',
      ingredients: product.ingredients || [],
      // Inventory management according to PRD
      inventory: {
        policy: {
          start_date: product.inventory?.policy?.start_date || new Date().toISOString().split('T')[0],
          end_date: product.inventory?.policy?.end_date || null,
          min_order_ahead: product.inventory?.policy?.min_order_ahead || { value: 3, unit: 'days' },
          same_day_allowed: product.inventory?.policy?.same_day_allowed || false
        },
        template: product.inventory?.template || [
          { weekday: 0, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' },
          { weekday: 1, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' },
          { weekday: 2, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' },
          { weekday: 3, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' },
          { weekday: 4, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' },
          { weekday: 5, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' },
          { weekday: 6, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' }
        ],
        scheduled: product.inventory?.scheduled || [],
        history: product.inventory?.history || []
      }
    };

    // Auto-generate missing scheduled dates and migrate past dates to history according to PRD
    const generatedData = generateMissingScheduledDates(initialData);
    initialData.inventory.scheduled = generatedData.scheduled;
    initialData.inventory.history = generatedData.history;

    return initialData;
  });



  // Save generated dates and migrated history to database after component mounts
  useEffect(() => {
    const saveGeneratedData = async () => {
      const originalScheduled = product.inventory?.scheduled || [];
      const currentScheduled = editData.inventory?.scheduled || [];
      const originalHistory = product.inventory?.history || [];
      const currentHistory = editData.inventory?.history || [];
      
      // Check if there are new scheduled dates or history changes that need to be saved
      const newScheduledDates = currentScheduled.filter(date => 
        !originalScheduled.find(original => original.service_date === date.service_date)
      );
      
      const newHistoryDates = currentHistory.filter(date => 
        !originalHistory.find(original => original.service_date === date.service_date)
      );
      
      const hasChanges = newScheduledDates.length > 0 || newHistoryDates.length > 0;
      
      if (hasChanges) {
        try {
          await updateProduct(product.id, editData);
          if (onUpdate) {
            onUpdate(product.id, editData);
          }
        } catch (error) {
          console.error('❌ [ProductCard] Error saving data changes:', error);
        }
      }
    };

    saveGeneratedData();
  }, []); // Only run once when component mounts

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStockStatus = (product) => {
    // Check if product has the new inventory structure
    if (product.inventory && product.inventory.scheduled) {
      const currentDate = new Date().toISOString().split('T')[0];
      const futureScheduled = product.inventory.scheduled.filter(item => 
        item.service_date >= currentDate
      );
      
      const totalAvailable = futureScheduled.reduce((sum, item) => 
        sum + (item.available_total || 0), 0
      );
      
      if (totalAvailable === 0) return { text: 'Out of Stock', color: COLORS.ERROR_RED };
      if (totalAvailable <= 10) return { text: `Low Stock (${totalAvailable})`, color: COLORS.WARNING_ORANGE };
      return { text: `In Stock (${totalAvailable})`, color: COLORS.SUCCESS_GREEN };
    }

    // Fallback to legacy stock calculation
    const stock = Number(product.stock) || 0;
    if (stock === 0) return { text: 'Out of Stock', color: COLORS.ERROR_RED };
    if (stock < 10) return { text: `Low Stock (${stock})`, color: COLORS.WARNING_ORANGE };
    return { text: `In Stock (${stock})`, color: COLORS.SUCCESS_GREEN };
  };

  const getStatusText = (product) => {
    const isActive = product.published === true || product.status === true;
    return isActive ? 'Active' : 'Inactive';
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    setIsSaving(true);
    try {
      // Regenerate scheduled dates one final time before saving to ensure everything is synced
      const dataToSave = { ...editData };
      const generatedData = generateMissingScheduledDates(dataToSave);
      dataToSave.inventory.scheduled = generatedData.scheduled;
      dataToSave.inventory.history = generatedData.history;
      
      // Final pass: Correct ALL weekday values from service_date to ensure accuracy
      console.log('🔧 [ProductCard] Starting weekday correction pass...');
      console.log('🔧 [ProductCard] Before correction:', dataToSave.inventory.scheduled.slice(0, 5).map(s => `${s.service_date}: weekday ${s.weekday}`).join(', '));
      
      dataToSave.inventory.scheduled = dataToSave.inventory.scheduled.map(scheduled => {
        // Always recalculate weekday from service_date string (don't trust stored weekday)
        const [year, month, day] = scheduled.service_date.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day, 12, 0, 0, 0);
        const correctWeekday = dateObj.getDay();
        
        // Debug logging for weekday correction
        if (scheduled.weekday !== correctWeekday) {
          console.log(`🔧 [ProductCard] Correcting weekday for ${scheduled.service_date}: ${scheduled.weekday} -> ${correctWeekday} (date: ${dateObj.toDateString()})`);
        }
        
        return {
          ...scheduled,
          weekday: correctWeekday
        };
      });
      
      // Verify correction was applied
      console.log('✅ [ProductCard] Weekday correction complete. After correction:', 
        dataToSave.inventory.scheduled.slice(0, 5).map(s => `${s.service_date}: weekday ${s.weekday}`).join(', '));
      
      await updateProduct(product.id, dataToSave);
      if (onUpdate) {
        onUpdate(product.id, dataToSave);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Failed to update product. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setEditData({
      name: product.name || '',
      description: product.description || '',
      price: product.price || 0,
      stock: product.stock || 0,
      status: product.status || false,
      listingType: product.listingType || '',
      itemCategory: product.itemCategory || '',
      cuisine: product.cuisine || '',
      courseType: product.courseType || '',
      dishType: product.dishType || '',
      ingredients: product.ingredients || [],
      // Inventory management according to PRD
      inventory: {
        policy: {
          start_date: product.inventory?.policy?.start_date || new Date().toISOString().split('T')[0],
          end_date: product.inventory?.policy?.end_date || null,
          min_order_ahead: product.inventory?.policy?.min_order_ahead || { value: 3, unit: 'days' },
          same_day_allowed: product.inventory?.policy?.same_day_allowed || false
        },
        template: product.inventory?.template || [
          { weekday: 0, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' },
          { weekday: 1, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' },
          { weekday: 2, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' },
          { weekday: 3, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' },
          { weekday: 4, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' },
          { weekday: 5, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' },
          { weekday: 6, enabled: true, default_max_total: 5, start_time_local: '08:00', end_time_local: '18:00' }
        ],
        scheduled: product.inventory?.scheduled || [],
        history: product.inventory?.history || []
      }
    });
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  // Helper functions for inventory management
  const getWeekdayName = (weekday) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[weekday];
  };

  // Helper function to get display value from taxonomy
  const getDisplayValue = (category, value) => {
    if (!value || !taxonomyData[category]) return value;
    
    const taxonomyItem = taxonomyData[category].find(item => item.value === value);
    return taxonomyItem ? taxonomyItem.display : value;
  };

  const getShortWeekdayName = (weekday) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[weekday];
  };

  const calculateMonthlyTotals = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    // Ensure scheduled is always an array
    const scheduled = Array.isArray(editData.inventory?.scheduled) ? editData.inventory.scheduled : [];
    
    const monthlyScheduled = scheduled.filter(item => {
      const itemDate = new Date(item.service_date);
      return itemDate.getMonth() === currentMonth && itemDate.getFullYear() === currentYear;
    });



    const totals = {
      planned: monthlyScheduled.reduce((sum, item) => sum + (item.max_total || 0), 0),
      ordered: monthlyScheduled.reduce((sum, item) => sum + (item.ordered_total || 0), 0),
      available: monthlyScheduled.reduce((sum, item) => sum + (item.available_total || 0), 0)
    };


    return totals;
  };



  const updateInventoryField = (section, field, value) => {
    setEditData(prev => {
      const updatedData = {
        ...prev,
        inventory: {
          ...prev.inventory,
          [section]: {
            ...prev.inventory[section],
            [field]: value
          }
        }
      };
      
      // If updating policy start_date or end_date, regenerate scheduled dates
      if (section === 'policy' && (field === 'start_date' || field === 'end_date')) {
        const generatedData = generateMissingScheduledDates(updatedData);
        updatedData.inventory.scheduled = generatedData.scheduled;
        updatedData.inventory.history = generatedData.history;
      }
      
      return updatedData;
    });
  };

  const updateInventoryArray = (section, weekday, field, value) => {
    setEditData(prev => {
      const updatedData = {
        ...prev,
        inventory: {
          ...prev.inventory,
          [section]: prev.inventory[section].map((item) => 
            item.weekday === weekday ? { ...item, [field]: value } : item
          )
        }
      };

      // If updating template enabled status, handle disabled state
      if (section === 'template' && field === 'enabled') {
        const isEnabled = value;
        
        // If disabling a template, also reset its default_max_total to 0
        if (!isEnabled) {
          updatedData.inventory.template = updatedData.inventory.template.map(template => 
            template.weekday === weekday 
              ? { ...template, default_max_total: 0 }
              : template
          );
        }
        
        // Regenerate scheduled dates based on updated templates
        const generatedData = generateMissingScheduledDates(updatedData);
        updatedData.inventory.scheduled = generatedData.scheduled;
        updatedData.inventory.history = generatedData.history;
      }
      // If updating template start_time_local or end_time_local, update all template-sourced dates for that weekday
      else if (section === 'template' && (field === 'start_time_local' || field === 'end_time_local')) {
        // Update all existing template-sourced scheduled dates for this weekday
        updatedData.inventory.scheduled = prev.inventory.scheduled.map(scheduled => {
          // Always recalculate weekday from service_date (don't trust stored weekday)
          // Parse the date string directly and normalize to noon to avoid timezone issues
          const [year, month, day] = scheduled.service_date.split('-').map(Number);
          const dateObj = new Date(year, month - 1, day, 12, 0, 0, 0); // month is 0-indexed, set to noon
          const actualWeekday = dateObj.getDay();
          const isMatch = actualWeekday === weekday;
          
          if (isMatch && scheduled.source === 'template') {
            const template = updatedData.inventory.template.find(t => t.weekday === weekday);
            return {
              ...scheduled,
              weekday: actualWeekday, // Fix the stored weekday to the correct value
              [field]: value,
              start_time_local: template ? (template.start_time_local || '08:00') : scheduled.start_time_local,
              end_time_local: template ? (template.end_time_local || '18:00') : scheduled.end_time_local
            };
          }
          return scheduled;
        });
      }
      // If updating template default_max_total, update existing scheduled dates for that weekday
      else if (section === 'template' && field === 'default_max_total') {
        const newMaxTotal = value;
        
        // First, update the template itself
        updatedData.inventory.template = updatedData.inventory.template.map(template => 
          template.weekday === weekday 
            ? { ...template, default_max_total: newMaxTotal }
            : template
        );
        
        // Update ALL existing scheduled dates for this weekday to match the new template value
        updatedData.inventory.scheduled = prev.inventory.scheduled.map(scheduled => {
          // Always recalculate weekday from service_date (don't trust stored weekday - it might be wrong)
          // Parse the date string directly and normalize to noon to avoid timezone issues
          const [year, month, day] = scheduled.service_date.split('-').map(Number);
          const dateObj = new Date(year, month - 1, day, 12, 0, 0, 0); // month is 0-indexed, set to noon
          const actualWeekday = dateObj.getDay(); // 0 = Sunday, 1 = Monday, etc.
          const isMatch = actualWeekday === weekday;
          
          if (isMatch && scheduled.source === 'template') {
            const template = updatedData.inventory.template.find(t => t.weekday === weekday);
            return {
              ...scheduled,
              weekday: actualWeekday, // Fix the stored weekday to the correct value
              enabled: template ? template.enabled : scheduled.enabled,
              max_total: template && template.enabled ? newMaxTotal : 0,
              available_total: template && template.enabled ? Math.max(0, newMaxTotal - (scheduled.ordered_total || 0)) : 0,
              start_time_local: template ? (template.start_time_local || '08:00') : scheduled.start_time_local,
              end_time_local: template ? (template.end_time_local || '18:00') : scheduled.end_time_local,
              source: 'template'
            };
          }
          return scheduled;
        });
        
        // Regenerate to ensure all dates are synced
        const generatedData = generateMissingScheduledDates(updatedData);
        updatedData.inventory.scheduled = generatedData.scheduled;
        updatedData.inventory.history = generatedData.history;
      }

      return updatedData;
    });
  };





  const toggleWeekdayExpanded = (weekday) => {
    setExpandedWeekdays(prev => 
      prev.includes(weekday) 
        ? prev.filter(w => w !== weekday)
        : [...prev, weekday]
    );
  };

  const getWeekdayDates = (weekday) => {
    // Use store timezone for consistent date calculations
    const storeTimezone = product.storeTimezone || 'America/New_York';
    const now = new Date();
    const storeDate = new Date(now.toLocaleString("en-US", {timeZone: storeTimezone}));
    const dates = [];
    
    // Get next 4 occurrences of this weekday
    for (let week = 0; week < 4; week++) {
      // Calculate the target date in the store's timezone
      const targetDate = new Date(storeDate);
      const daysUntilWeekday = (weekday - targetDate.getDay() + 7) % 7;
      targetDate.setDate(targetDate.getDate() + daysUntilWeekday + (week * 7));
      
      // Convert to store timezone string
      const dateString = targetDate.toLocaleDateString('en-CA', {timeZone: storeTimezone}); // YYYY-MM-DD format
      const calculatedWeekday = targetDate.getDay();
      
              dates.push({
          date: dateString,
          formatted: targetDate.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            weekday: 'short'
          })
        });
    }
    
    return dates;
  };

  const getScheduledDate = (dateString) => {
    const scheduledDate = editData.inventory?.scheduled?.find(s => s.service_date === dateString) || null;
    return scheduledDate;
  };

  const updateScheduledDate = (dateString, maxTotal) => {
    const existing = editData.inventory?.scheduled?.find(s => s.service_date === dateString);
    
    // Calculate weekday directly from date string to avoid timezone issues
    // Parse date string directly: "YYYY-MM-DD"
    const [year, month, day] = dateString.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day, 12, 0, 0, 0); // month is 0-indexed, set to noon
    const calculatedWeekday = dateObj.getDay();
    
    const template = editData.inventory.template.find(t => 
      t.weekday === calculatedWeekday
    );

    if (existing) {
      // Update existing scheduled date
      setEditData(prev => ({
        ...prev,
        inventory: {
          ...prev.inventory,
          scheduled: prev.inventory.scheduled.map(s => 
            s.service_date === dateString 
              ? { 
                  ...s, 
                  max_total: maxTotal, 
                  available_total: maxTotal - (s.ordered_total || 0),
                  source: maxTotal === template?.default_max_total ? 'template' : 'manual'
                }
              : s
          )
        }
      }));
    } else if (template && template.enabled) {
      // Create new scheduled date when user changes from template value
      const newScheduled = {
        service_date: dateString,
        weekday: calculatedWeekday, // Use the correctly calculated weekday
        source: maxTotal === template.default_max_total ? 'template' : 'manual',
        enabled: true,
        max_total: maxTotal,
        ordered_total: 0,
        available_total: maxTotal,
        start_time_local: template.start_time_local || '08:00',
        end_time_local: template.end_time_local || '18:00'
      };

      setEditData(prev => ({
        ...prev,
        inventory: {
          ...prev.inventory,
          scheduled: [...(prev.inventory.scheduled || []), newScheduled]
        }
      }));
    }
  };

  // Ingredient management functions
  const searchIngredients = async (query) => {
    if (query.length < 3) {
      setIngredientResults([]);
      return;
    }

    setIsSearchingIngredients(true);
    try {
      // Use the Open Food Facts API to search for ingredients
      let data = [];
      
      try {
        // Search for products using the products API
        const response = await fetch(`https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(query)}&search_simple=1&action=process&json=1&page_size=50&fields=product_name,brands,image_url`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch ingredients');
        }
        
        const apiData = await response.json();
        
        if (apiData && apiData.products && apiData.products.length > 0) {
          // Extract and process ingredients from products
          const ingredientsMap = new Map();
          
          apiData.products.forEach(product => {
            // Use the product name as the ingredient
            const productName = product.product_name || '';
            const brand = product.brands || '';
            
            if (productName) {
              // Create a key that includes both brand and product name
              const key = `${brand} ${productName}`.toLowerCase().trim();
              
              if (!ingredientsMap.has(key)) {
                ingredientsMap.set(key, {
                  id: `en:${key.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`,
                  name: productName,
                  category: 'Food',
                  products: 1,
                  brand: brand || 'Unknown'
                });
              } else {
                // Increment product count for existing ingredient
                const existing = ingredientsMap.get(key);
                existing.products += 1;
              }
            }
          });
          
          // Convert map to array and sort by popularity
          data = Array.from(ingredientsMap.values())
            .sort((a, b) => b.products - a.products)
            .slice(0, 20); // Top 20 results
        }
        
        // If no results from API, fall back to mock data
        if (data.length === 0) {
          data = getMockIngredientsData();
        }
        
      } catch (apiError) {
        console.warn('API fetch failed, using mock data:', apiError);
        // Fallback to mock data for testing
        data = getMockIngredientsData();
      }

      if (data && data.length > 0) {
        // Split query into individual words for more flexible matching
        const queryWords = query.toLowerCase().split(/\s+/).filter(word => word.length > 0);
        
        // Filter ingredients that match the query with flexible matching
        const matchingIngredients = data.filter(ingredient => {
          const searchText = query.toLowerCase();
          const ingredientText = (ingredient.name || ingredient.text || '').toLowerCase();
          const ingredientId = (ingredient.id || '').toLowerCase();
          const brandText = (ingredient.brand || '').toLowerCase();
          
          // Create combined text for searching
          const combinedText = `${brandText} ${ingredientText}`.toLowerCase();
          
          // Exact phrase match (highest priority)
          if (combinedText.includes(searchText) || ingredientText.includes(searchText) || brandText.includes(searchText)) {
            return true;
          }
          
          // Word-by-word matching (more flexible)
          if (queryWords.length > 1) {
            // Check if ALL query words are found somewhere in the combined text
            const allWordsFound = queryWords.every(word => 
              combinedText.includes(word) || ingredientText.includes(word) || brandText.includes(word)
            );
            
            if (allWordsFound) {
              return true;
            }
          }
          
          // Partial word matching (for typos and partial searches)
          const hasPartialMatch = queryWords.some(word => {
            // Check if any word partially matches any part of the text
            return combinedText.includes(word) || 
                   ingredientText.includes(word) || 
                   brandText.includes(word) ||
                   // Check for partial matches (e.g., "king" matches "king arthur")
                   combinedText.split(/\s+/).some(textWord => textWord.startsWith(word) || word.startsWith(textWord));
          });
          
          return hasPartialMatch;
        });

        // Rank results by relevance and popularity
        const rankedIngredients = matchingIngredients
          .map(ingredient => ({
            ...ingredient,
            relevanceScore: calculateRelevanceScore(ingredient, query)
          }))
          .sort((a, b) => b.relevanceScore - a.relevanceScore)
          .slice(0, 10); // Top 10 results

        setIngredientResults(rankedIngredients);
      } else {
        setIngredientResults([]);
      }
    } catch (error) {
      console.error('Error searching ingredients:', error);
      setIngredientResults([]);
    } finally {
      setIsSearchingIngredients(false);
    }
  };

  // Helper function to calculate relevance score
  const calculateRelevanceScore = (ingredient, query) => {
    const queryLower = query.toLowerCase();
    const queryWords = queryLower.split(/\s+/).filter(word => word.length > 0);
    const nameLower = (ingredient.name || ingredient.text || '').toLowerCase();
    const brandLower = (ingredient.brand || '').toLowerCase();
    const combinedText = `${brandLower} ${nameLower}`.toLowerCase();
    
    let score = 0;
    
    // Exact phrase match (highest priority)
    if (combinedText === queryLower) {
      score += 200;
    } else if (nameLower === queryLower) {
      score += 150;
    } else if (brandLower === queryLower) {
      score += 120;
    }
    
    // Starts with match
    if (combinedText.startsWith(queryLower)) {
      score += 80;
    } else if (nameLower.startsWith(queryLower)) {
      score += 60;
    } else if (brandLower.startsWith(queryLower)) {
      score += 50;
    }
    
    // Contains match
    if (combinedText.includes(queryLower)) {
      score += 40;
    } else if (nameLower.includes(queryLower)) {
      score += 30;
    } else if (brandLower.includes(queryLower)) {
      score += 25;
    }
    
    // Word-by-word matching (for multi-word queries)
    if (queryWords.length > 1) {
      const allWordsFound = queryWords.every(word => 
        combinedText.includes(word) || nameLower.includes(word) || brandLower.includes(word)
      );
      
      if (allWordsFound) {
        score += 35; // Bonus for finding all words
      }
      
      // Check word order and proximity
      const wordPositions = queryWords.map(word => {
        const namePos = nameLower.indexOf(word);
        const brandPos = brandLower.indexOf(word);
        return Math.min(namePos >= 0 ? namePos : Infinity, brandPos >= 0 ? brandPos : Infinity);
      }).filter(pos => pos !== Infinity);
      
      if (wordPositions.length === queryWords.length) {
        // Bonus for words appearing in order
        const isInOrder = wordPositions.every((pos, i) => i === 0 || pos >= wordPositions[i - 1]);
        if (isInOrder) {
          score += 20;
        }
      }
    }
    
    // Partial word matching bonus
    queryWords.forEach(word => {
      if (combinedText.split(/\s+/).some(textWord => 
        textWord.startsWith(word) || word.startsWith(textWord)
      )) {
        score += 10;
      }
    });
    
    // Brand name priority (brand matches are often more specific)
    if (brandLower.includes(queryLower) || queryWords.some(word => brandLower.includes(word))) {
      score += 15;
    }
    
    // Popularity bonus (more products = higher score)
    score += Math.min(ingredient.products || 0, 10);
    
    return score;
  };

  // Mock ingredients data for fallback
  const getMockIngredientsData = () => {
    return [
      { id: '1', name: 'Flour', category: 'Baking', products: 5 },
      { id: '2', name: 'Sugar', category: 'Baking', products: 8 },
      { id: '3', name: 'Eggs', category: 'Dairy', products: 12 },
      { id: '4', name: 'Milk', category: 'Dairy', products: 15 },
      { id: '5', name: 'Butter', category: 'Dairy', products: 10 },
      { id: '6', name: 'Salt', category: 'Seasoning', products: 20 },
      { id: '7', name: 'Pepper', category: 'Seasoning', products: 18 },
      { id: '8', name: 'Olive Oil', category: 'Oil', products: 14 },
      { id: '9', name: 'Tomatoes', category: 'Vegetables', products: 16 },
      { id: '10', name: 'Onions', category: 'Vegetables', products: 13 },
      { id: '11', name: 'Garlic', category: 'Vegetables', products: 11 },
      { id: '12', name: 'Chicken Breast', category: 'Meat', products: 9 },
      { id: '13', name: 'Ground Beef', category: 'Meat', products: 7 },
      { id: '14', name: 'Rice', category: 'Grains', products: 6 },
      { id: '15', name: 'Pasta', category: 'Grains', products: 4 }
    ];
  };

  const addIngredient = (ingredient) => {
    // Check if ingredient already exists
    if (editData.ingredients.some(ing => ing.id === ingredient.id)) {
      return;
    }
    
    const newIngredients = [...editData.ingredients, ingredient];
    setEditData(prev => ({ ...prev, ingredients: newIngredients }));
    setIngredientSearch('');
    setIngredientResults([]);
  };

  const removeIngredient = (ingredientId) => {
    const newIngredients = editData.ingredients.filter(ing => ing.id !== ingredientId);
    setEditData(prev => ({ ...prev, ingredients: newIngredients }));
  };

  // Debounced search effect for ingredients
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (ingredientSearch.trim()) {
        searchIngredients(ingredientSearch.trim());
      } else {
        setIngredientResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [ingredientSearch]);

  const handleDelete = async (e) => {
    e.stopPropagation();
    const confirmMessage = `Deleting will remove "${product.name}" from your storefront and product list. Historical order data will remain. Do you want to continue?`;
    
    if (window.confirm(confirmMessage)) {
      setIsDeleting(true);
      try {
        console.log(`🔍 [ProductCard] Attempting to archive product: ${product.id}`);
        console.log(`🔍 [ProductCard] Product data:`, product);
        
        // Validate product ID
        if (!product.id) {
          throw new Error('Product ID is missing');
        }
        
        // Archive the product instead of deleting it
        const result = await archiveProduct(product.id);
        console.log(`✅ [ProductCard] Archive result:`, result);
        
        // Call the onDelete callback to update the parent component
        if (onDelete) {
          onDelete(product.id);
        }
      } catch (error) {
        console.error('Error archiving product:', error);
        
        // Show more specific error message based on the error type
        let errorMessage = 'Failed to archive product. Please try again.';
        
        if (error.code === 'permission-denied') {
          errorMessage = 'Permission denied. You may not have the right to archive this product.';
        } else if (error.code === 'not-found') {
          errorMessage = 'Product not found. It may have already been deleted.';
        } else if (error.code === 'unavailable') {
          errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
        } else if (error.message) {
          errorMessage = `Archive failed: ${error.message}`;
        }
        
        alert(errorMessage);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const stockStatus = getStockStatus(product);
  const statusText = getStatusText(product);

  return (
    <div
      className={`product-card ${className}`}
      style={{
        background: COLORS.NEUTRAL_WHITE,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: isExpanded ? `2px solid ${COLORS.PRIMARY_BROWN}` : 'none',
        transition: 'all 0.2s ease'
      }}
    >
      {/* Product Info Header */}
      <div 
        onClick={onToggleExpand}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          cursor: 'pointer',
          gap: 16,
          minHeight: 84
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpand();
          }
        }}
        aria-expanded={isExpanded}
        aria-label={`${product.name} - ${statusText} - ${stockStatus.text}. Click to expand for more options.`}
      >
        {/* Product Thumbnail */}
        <div style={{ position: 'relative' }}>
          <img
            src={product.imageUrl || product.image || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjMwIiB5PSIzMCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5OTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+Cjwvc3ZnPgo='}
            alt={product.name}
            style={{
              width: 60,
              height: 60,
              objectFit: 'cover',
              borderRadius: 8,
              backgroundColor: COLORS.NEUTRAL_BG
            }}
            onError={(e) => {
              // Prevent infinite loop by using a data URL
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjMwIiB5PSIzMCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5OTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+Cjwvc3ZnPgo=';
            }}
          />
          {/* Status indicator */}
          <div
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: product.published ? COLORS.SUCCESS_GREEN : COLORS.ACCENT_GRAY,
              border: `2px solid ${COLORS.NEUTRAL_WHITE}`
            }}
            aria-label={`Product is ${statusText.toLowerCase()}`}
          />
        </div>
        
        {/* Product Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 
            style={{ 
              ...FONTS.heading, 
              fontSize: 14, 
              color: COLORS.NEUTRAL_TEXT,
              margin: '0 0 4px 0',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={product.name}
          >
            {product.name}
          </h3>
          
          <div style={{ 
            fontSize: 12, 
            color: COLORS.ACCENT_GRAY,
            marginBottom: 4,
            ...FONTS.body
          }}>
            {statusText} • {getStockStatus(product).text}
          </div>
          
          <div style={{ 
            fontSize: 14, 
            color: COLORS.PRIMARY_BROWN,
            fontWeight: 600,
            ...FONTS.body
          }}>
            {formatCurrency(product.price)}
          </div>
        </div>
        
        {/* Expand/Collapse Icon */}
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            color: COLORS.ACCENT_GRAY,
            transition: 'transform 0.2s ease'
          }}
        >
          {isExpanded ? (
            <ChevronUp size={16} aria-hidden="true" />
          ) : (
            <ChevronDown size={16} aria-hidden="true" />
          )}
        </div>
      </div>
      
      {/* Expanded State */}
      {isExpanded && (
        <div 
          style={{
            borderTop: '1px solid #E0E0E0',
            padding: '16px',
            animation: 'slideDown 0.2s ease'
          }}
        >
          {isEditing ? (
            /* Edit Mode */
            <div>
              {/* Active Toggle */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: editData.published ? COLORS.SUCCESS_GREEN + '10' : COLORS.ERROR_RED + '10',
                border: `1px solid ${editData.published ? COLORS.SUCCESS_GREEN + '30' : COLORS.ERROR_RED + '30'}`,
                borderRadius: 8,
                marginBottom: 16
              }}>
                <div>
                  <div style={{ 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: COLORS.NEUTRAL_TEXT,
                    marginBottom: 2,
                    ...FONTS.heading 
                  }}>
                    Product Status
                  </div>
                  <div style={{ 
                    fontSize: 12, 
                    color: COLORS.ACCENT_GRAY,
                    ...FONTS.body 
                  }}>
                                         {editData.status ? 'Active - Product is visible to customers' : 'Inactive - Product is hidden from customers'}
                  </div>
                </div>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  gap: 8
                }}>
                  <input
                    type="checkbox"
                    checked={editData.status}
                    onChange={(e) => handleInputChange('status', e.target.checked)}
                    style={{ display: 'none' }}
                  />
                  <div style={{
                    width: 48,
                    height: 24,
                    background: editData.status ? COLORS.SUCCESS_GREEN : COLORS.ACCENT_GRAY,
                    borderRadius: 12,
                    position: 'relative',
                    transition: 'background-color 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '2px'
                  }}>
                    <div style={{
                      width: 20,
                      height: 20,
                      background: 'white',
                      borderRadius: '50%',
                      transform: editData.status ? 'translateX(24px)' : 'translateX(0px)',
                      transition: 'transform 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} />
                  </div>
                </label>
              </div>

              {/* Basic Information */}
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px 0', ...FONTS.heading }}>
                  Basic Information
                </h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* Name */}
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block', ...FONTS.body }}>
                      Product Name
                    </label>
                    <input
                      type="text"
                      value={editData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #E0E0E0',
                        borderRadius: 6,
                        fontSize: 12,
                        ...FONTS.body
                      }}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block', ...FONTS.body }}>
                      Description
                    </label>
                    <textarea
                      value={editData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #E0E0E0',
                        borderRadius: 6,
                        fontSize: 12,
                        resize: 'vertical',
                        ...FONTS.body
                      }}
                    />
                  </div>

                  {/* Categories */}
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px 0', ...FONTS.heading }}>
                      Categories
                    </h4>
                   
                   <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                     <div style={{ display: 'flex', gap: 12 }}>
                       <div style={{ flex: 1 }}>
                         <label style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block', ...FONTS.body }}>
                           Listing Type
                         </label>
                         <select
                           value={editData.listingType || ''}
                           onChange={(e) => handleInputChange('listingType', e.target.value)}
                           style={{
                             width: '100%',
                             padding: '8px 12px',
                             border: '1px solid #E0E0E0',
                             borderRadius: 6,
                             fontSize: 12,
                             background: '#FFFFFF',
                             ...FONTS.body
                           }}
                         >
                           <option value="">Select Listing Type</option>
                           {taxonomyData.listingType
                             .sort((a, b) => a.display.localeCompare(b.display))
                             .map(item => (
                               <option key={item.value} value={item.value}>
                                 {item.display}
                               </option>
                             ))}
                         </select>
                       </div>
                       <div style={{ flex: 1 }}>
                         <label style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block', ...FONTS.body }}>
                           Item Category
                         </label>
                         <select
                           value={editData.itemCategory || ''}
                           onChange={(e) => handleInputChange('itemCategory', e.target.value)}
                           style={{
                             width: '100%',
                             padding: '8px 12px',
                             border: '1px solid #E0E0E0',
                             borderRadius: 6,
                             fontSize: 12,
                             background: '#FFFFFF',
                             ...FONTS.body
                           }}
                         >
                           <option value="">Select Item Category</option>
                           {taxonomyData.itemCategory
                             .sort((a, b) => a.display.localeCompare(b.display))
                             .map(item => (
                               <option key={item.value} value={item.value}>
                                 {item.display}
                               </option>
                             ))}
                         </select>
                       </div>
                     </div>

                     <div style={{ display: 'flex', gap: 12 }}>
                       <div style={{ flex: 1 }}>
                         <label style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block', ...FONTS.body }}>
                           Cuisine
                         </label>
                         <select
                           value={editData.cuisine || ''}
                           onChange={(e) => handleInputChange('cuisine', e.target.value)}
                           style={{
                             width: '100%',
                             padding: '8px 12px',
                             border: '1px solid #E0E0E0',
                             borderRadius: 6,
                             fontSize: 12,
                             background: '#FFFFFF',
                             ...FONTS.body
                           }}
                         >
                           <option value="">Select Cuisine</option>
                           {taxonomyData.cuisine
                             .sort((a, b) => a.display.localeCompare(b.display))
                             .map(item => (
                               <option key={item.value} value={item.value}>
                                 {item.display}
                               </option>
                             ))}
                         </select>
                       </div>
                       <div style={{ flex: 1 }}>
                         <label style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block', ...FONTS.body }}>
                           Course Type
                         </label>
                         <select
                           value={editData.courseType || ''}
                           onChange={(e) => handleInputChange('courseType', e.target.value)}
                           style={{
                             width: '100%',
                             padding: '8px 12px',
                             border: '1px solid #E0E0E0',
                             borderRadius: 6,
                             fontSize: 12,
                             background: '#FFFFFF',
                             ...FONTS.body
                           }}
                         >
                           <option value="">Select Course Type</option>
                           {taxonomyData.courseType
                             .sort((a, b) => a.display.localeCompare(b.display))
                             .map(item => (
                               <option key={item.value} value={item.value}>
                                 {item.display}
                               </option>
                             ))}
                         </select>
                       </div>
                     </div>

                     <div>
                       <label style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block', ...FONTS.body }}>
                         Dish Type
                       </label>
                       <select
                         value={editData.dishType || ''}
                         onChange={(e) => handleInputChange('dishType', e.target.value)}
                         style={{
                           width: '100%',
                           padding: '8px 12px',
                           border: '1px solid #E0E0E0',
                           borderRadius: 6,
                           fontSize: 12,
                           background: '#FFFFFF',
                           ...FONTS.body
                         }}
                                                >
                           <option value="">Select Dish Type</option>
                           {taxonomyData.dishType
                             .sort((a, b) => a.display.localeCompare(b.display))
                             .map(item => (
                               <option key={item.value} value={item.value}>
                                 {item.display}
                               </option>
                             ))}
                         </select>
                     </div>

                     {/* Ingredients */}
                     <div>
                       <label style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block', ...FONTS.body }}>
                         Ingredients
                       </label>
                       
                       {/* Ingredient Search Input */}
                       <div style={{ position: 'relative', marginBottom: '8px' }}>
                         <input
                           type="text"
                           value={ingredientSearch}
                           onChange={(e) => setIngredientSearch(e.target.value)}
                           placeholder="Search ingredients (type at least 3 characters)"
                           style={{
                             width: '100%',
                             padding: '8px 12px',
                             paddingLeft: '32px',
                             border: '1px solid #E0E0E0',
                             borderRadius: 6,
                             fontSize: 12,
                             ...FONTS.body
                           }}
                         />
                         <Search
                           size={16}
                           style={{
                             position: 'absolute',
                             left: '8px',
                             top: '50%',
                             transform: 'translateY(-50%)',
                             color: COLORS.ACCENT_GRAY
                           }}
                         />
                       </div>

                       {/* Search Results Dropdown */}
                       {ingredientResults.length > 0 && (
                         <div style={{
                           border: '1px solid #E0E0E0',
                           borderRadius: 6,
                           backgroundColor: COLORS.NEUTRAL_WHITE,
                           maxHeight: '150px',
                           overflowY: 'auto',
                           marginBottom: '8px',
                           boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                         }}>
                           {ingredientResults.map((ingredient) => (
                             <div
                               key={ingredient.id}
                               onClick={() => addIngredient(ingredient)}
                               style={{
                                 padding: '8px 12px',
                                 cursor: 'pointer',
                                 borderBottom: '1px solid #f0f0f0',
                                 fontSize: 12,
                                 ...FONTS.body
                               }}
                               onMouseEnter={(e) => {
                                 e.target.style.backgroundColor = COLORS.NEUTRAL_BG;
                               }}
                               onMouseLeave={(e) => {
                                 e.target.style.backgroundColor = COLORS.NEUTRAL_WHITE;
                               }}
                             >
                               <div style={{ fontWeight: 500, color: COLORS.NEUTRAL_TEXT }}>
                                 {ingredient.brand && ingredient.brand !== 'Unknown' ? `${ingredient.brand} - ${ingredient.name}` : ingredient.name}
                               </div>
                               {ingredient.brand && ingredient.brand !== 'Unknown' && (
                                 <div style={{ fontSize: 10, color: COLORS.ACCENT_GRAY, marginTop: '2px' }}>
                                   {ingredient.brand}
                                 </div>
                               )}
                             </div>
                           ))}
                         </div>
                       )}

                       {/* Loading State */}
                       {isSearchingIngredients && (
                         <div style={{
                           padding: '8px 12px',
                           fontSize: 12,
                           color: COLORS.ACCENT_GRAY,
                           ...FONTS.body
                         }}>
                           Searching ingredients...
                         </div>
                       )}

                       {/* Selected Ingredients Chips */}
                       {editData.ingredients.length > 0 && (
                         <div style={{
                           display: 'flex',
                           flexWrap: 'wrap',
                           gap: '6px',
                           marginTop: '8px'
                         }}>
                           {editData.ingredients.map((ingredient) => (
                             <div
                               key={ingredient.id}
                               style={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: '4px',
                                 padding: '4px 8px',
                                 backgroundColor: COLORS.NEUTRAL_BG,
                                 borderRadius: '12px',
                                 fontSize: 11,
                                 color: COLORS.NEUTRAL_TEXT,
                                 ...FONTS.body
                               }}
                             >
                               <span>{ingredient.brand && ingredient.brand !== 'Unknown' ? `${ingredient.brand} - ${ingredient.name}` : ingredient.name}</span>
                               <button
                                 type="button"
                                 onClick={() => removeIngredient(ingredient.id)}
                                 style={{
                                   background: 'none',
                                   border: 'none',
                                   cursor: 'pointer',
                                   fontSize: 12,
                                   color: COLORS.ACCENT_GRAY,
                                   padding: '0',
                                   width: '14px',
                                   height: '14px',
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center'
                                 }}
                                 aria-label={`Remove ${ingredient.name}`}
                               >
                                 ×
                               </button>
                             </div>
                           ))}
                         </div>
                       )}
                     </div>
                   
                   {/* Allergy Warning */}
                   <div style={{
                     background: '#FFF3CD',
                     border: '1px solid #FFEAA7',
                     borderRadius: 6,
                     padding: '8px 12px',
                     marginTop: '8px'
                   }}>
                     <div style={{
                       display: 'flex',
                       alignItems: 'flex-start',
                       gap: '6px'
                     }}>
                       <span style={{
                         color: '#856404',
                         fontSize: '12px',
                         fontWeight: 600,
                         ...FONTS.body
                       }}>
                         ⚠️
                       </span>
                       <div style={{
                         fontSize: '11px',
                         color: '#856404',
                         lineHeight: '1.4',
                         ...FONTS.body
                       }}>
                         <strong>Important:</strong> Please be accurate with your ingredients. Allergies can be life-threatening, and customers rely on this information for their safety. We will not display all ingredients to users (keeping your recipes safe), but we will showcase allergy warnings.
                       </div>
                     </div>
                   </div>
                   </div>
                 </div>

                  {/* Price and Cost */}
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block', ...FONTS.body }}>
                        Price per Unit ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editData.price}
                        onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #E0E0E0',
                          borderRadius: 6,
                          fontSize: 12,
                          ...FONTS.body
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block', ...FONTS.body }}>
                        Cost per Unit ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editData.cost || 0}
                        onChange={(e) => handleInputChange('cost', parseFloat(e.target.value) || 0)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #E0E0E0',
                          borderRadius: 6,
                          fontSize: 12,
                          ...FONTS.body
                        }}
                      />
                    </div>
                  </div>

                  {/* Margin Calculation */}
                  <div style={{ 
                    background: COLORS.NEUTRAL_BG, 
                    padding: 12, 
                    borderRadius: 6, 
                    marginTop: 8 
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 500, color: COLORS.NEUTRAL_TEXT, ...FONTS.body }}>
                        Margin %
                      </span>
                      <span style={{ 
                        fontSize: 14, 
                        fontWeight: 600, 
                        color: editData.price > 0 && editData.cost > 0 ? COLORS.SUCCESS_GREEN : COLORS.ACCENT_GRAY,
                        ...FONTS.body 
                      }}>
                        {(() => {
                          const price = parseFloat(editData.price) || 0;
                          const cost = parseFloat(editData.cost) || 0;
                          if (price > 0 && cost > 0) {
                            const margin = ((price - cost) / price) * 100;
                            return `${margin.toFixed(1)}%`;
                          }
                          return '0.0%';
                        })()}
                      </span>
                    </div>
                    <div style={{ 
                      fontSize: 11, 
                      color: COLORS.ACCENT_GRAY, 
                      marginTop: 4,
                      ...FONTS.body 
                    }}>
                      Profit: ${(() => {
                        const price = parseFloat(editData.price) || 0;
                        const cost = parseFloat(editData.cost) || 0;
                        return (price - cost).toFixed(2);
                      })()}
                    </div>
                  </div>
                </div>
              </div>

                             {/* Inventory Management - PRD Compliant */}
               <div style={{ marginBottom: 16 }}>
                 <h4 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px 0', ...FONTS.heading }}>
                   Inventory Management
                 </h4>
                 
                 {/* Monthly Summary */}
                 <div style={{ 
                   background: COLORS.NEUTRAL_BG, 
                   padding: 12, 
                   borderRadius: 8, 
                   marginBottom: 16 
                 }}>
                   <h5 style={{ fontSize: 12, fontWeight: 600, margin: '0 0 8px 0', ...FONTS.heading }}>
                     Monthly Summary
                   </h5>
                   {(() => {
                     const totals = calculateMonthlyTotals();
                     return (
                       <div style={{ display: 'flex', gap: 16, fontSize: 11, ...FONTS.body }}>
                         <div>
                           <span style={{ color: COLORS.ACCENT_GRAY }}>Planned: </span>
                           <span style={{ fontWeight: 600, color: COLORS.PRIMARY_BROWN }}>{totals.planned}</span>
                         </div>
                         <div>
                           <span style={{ color: COLORS.ACCENT_GRAY }}>Ordered: </span>
                           <span style={{ fontWeight: 600, color: COLORS.ERROR_RED }}>{totals.ordered}</span>
                         </div>
                         <div>
                           <span style={{ color: COLORS.ACCENT_GRAY }}>Available: </span>
                           <span style={{ fontWeight: 600, color: COLORS.SUCCESS_GREEN }}>{totals.available}</span>
                         </div>
                       </div>
                     );
                   })()}
                 </div>

                 {/* Product Policy */}
                 <div style={{ marginBottom: 16 }}>
                   <h5 style={{ fontSize: 12, fontWeight: 600, margin: '0 0 8px 0', ...FONTS.heading }}>
                     Product Policy
                   </h5>
                   <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                     <div style={{ display: 'flex', gap: 8 }}>
                       <div style={{ flex: 1 }}>
                         <label style={{ fontSize: 11, fontWeight: 500, marginBottom: 2, display: 'block', ...FONTS.body }}>
                           Start Date
                         </label>
                         <input
                           type="date"
                           value={editData.inventory?.policy?.start_date || new Date().toISOString().split('T')[0]}
                           onChange={(e) => updateInventoryField('policy', 'start_date', e.target.value)}
                           style={{
                             width: '100%',
                             padding: '6px 8px',
                             border: '1px solid #E0E0E0',
                             borderRadius: 4,
                             fontSize: 11,
                             ...FONTS.body
                           }}
                         />
                       </div>
                       <div style={{ flex: 1 }}>
                         <label style={{ fontSize: 11, fontWeight: 500, marginBottom: 2, display: 'block', ...FONTS.body }}>
                           End Date
                         </label>
                         <input
                           type="date"
                           value={editData.inventory?.policy?.end_date || ''}
                           onChange={(e) => updateInventoryField('policy', 'end_date', e.target.value || null)}
                           style={{
                             width: '100%',
                             padding: '6px 8px',
                             border: '1px solid #E0E0E0',
                             borderRadius: 4,
                             fontSize: 11,
                             ...FONTS.body
                           }}
                         />
                       </div>
                     </div>
                     
                     <div style={{ display: 'flex', gap: 8 }}>
                       <div style={{ flex: 1 }}>
                         <label style={{ fontSize: 11, fontWeight: 500, marginBottom: 2, display: 'block', ...FONTS.body }}>
                           Min Order Ahead
                         </label>
                         <div style={{ fontSize: 10, color: COLORS.ACCENT_GRAY, marginBottom: 4, ...FONTS.body }}>
                           Set to 0 for immediate ordering
                         </div>
                         <div style={{ display: 'flex', gap: 4 }}>
                           <input
                             type="number"
                             min="0"
                             value={editData.inventory?.policy?.min_order_ahead?.value || 0}
                             onChange={(e) => updateInventoryField('policy', 'min_order_ahead', { 
                               value: parseInt(e.target.value) || 0, 
                               unit: editData.inventory?.policy?.min_order_ahead?.unit || 'days' 
                             })}
                             style={{
                               flex: 1,
                               padding: '6px 8px',
                               border: '1px solid #E0E0E0',
                               borderRadius: 4,
                               fontSize: 11,
                               ...FONTS.body
                             }}
                           />
                           <select
                             value={editData.inventory?.policy?.min_order_ahead?.unit || 'days'}
                             onChange={(e) => updateInventoryField('policy', 'min_order_ahead', { 
                               value: editData.inventory?.policy?.min_order_ahead?.value || 0, 
                               unit: e.target.value 
                             })}
                             style={{
                               padding: '6px 8px',
                               border: '1px solid #E0E0E0',
                               borderRadius: 4,
                               fontSize: 11,
                               minWidth: 80,
                               ...FONTS.body
                             }}
                           >
                             <option value="hours">Hours</option>
                             <option value="days">Days</option>
                           </select>
                         </div>
                       </div>

                     </div>

                     <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                       <div
                         onClick={() => updateInventoryField('policy', 'same_day_allowed', !editData.inventory?.policy?.same_day_allowed)}
                         style={{
                           width: 16,
                           height: 16,
                           border: '2px solid #6B7280',
                           borderRadius: 2,
                           background: editData.inventory?.policy?.same_day_allowed ? 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)' : '#FFFFFF',
                           cursor: 'pointer',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           position: 'relative'
                         }}
                       >
                         {editData.inventory?.policy?.same_day_allowed && (
                           <div style={{
                             width: 6,
                             height: 3,
                             border: '1.5px solid #FFFFFF',
                             borderTop: 'none',
                             borderRight: 'none',
                             transform: 'rotate(-45deg)',
                             position: 'absolute',
                             top: '5px',
                             left: '3px'
                           }} />
                         )}
                       </div>
                       <div 
                         onClick={() => updateInventoryField('policy', 'same_day_allowed', !editData.inventory?.policy?.same_day_allowed)}
                         style={{ fontSize: 11, fontWeight: 500, cursor: 'pointer', ...FONTS.body }}
                       >
                         Allow Same-Day Orders
                       </div>
                     </div>
                   </div>
                 </div>

                                   {/* Weekday Templates with Individual Dates */}
                  <div style={{ marginBottom: 16 }}>
                    <h5 style={{ fontSize: 12, fontWeight: 600, margin: '0 0 8px 0', ...FONTS.heading }}>
                      Availability
                    </h5>
                    <div style={{ fontSize: 11, color: COLORS.ACCENT_GRAY, marginBottom: 8, ...FONTS.body }}>
                      Set weekday availability and edit individual dates a month out
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(editData.inventory?.template || []).map((template, index) => {
                        const weekdayDates = getWeekdayDates(template.weekday);
                        console.log(`Rendering template for weekday ${template.weekday} (${getWeekdayName(template.weekday)}), default_max_total: ${template.default_max_total}`);
                        return (
                          <div key={template.weekday} style={{ 
                            border: '1px solid #E0E0E0', 
                            borderRadius: 6, 
                            padding: 8,
                            background: template.enabled ? COLORS.NEUTRAL_WHITE : COLORS.NEUTRAL_BG
                          }}>
                            {/* Template Header */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div
                                  onClick={() => updateInventoryArray('template', template.weekday, 'enabled', !template.enabled)}
                                  style={{
                                    width: 16,
                                    height: 16,
                                    border: '2px solid #6B7280',
                                    borderRadius: 2,
                                    background: template.enabled ? 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)' : '#FFFFFF',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative'
                                  }}
                                >
                                  {template.enabled && (
                                    <div style={{
                                      width: 6,
                                      height: 3,
                                      border: '1.5px solid #FFFFFF',
                                      borderTop: 'none',
                                      borderRight: 'none',
                                      transform: 'rotate(-45deg)',
                                      position: 'absolute',
                                      top: '5px',
                                      left: '3px'
                                    }} />
                                  )}
                                </div>
                                <span 
                                  onClick={() => updateInventoryArray('template', template.weekday, 'enabled', !template.enabled)}
                                  style={{ 
                                    fontSize: 11, 
                                    fontWeight: 600, 
                                    width: 60,
                                    color: template.enabled ? COLORS.NEUTRAL_TEXT : COLORS.ACCENT_GRAY,
                                    cursor: 'pointer',
                                    ...FONTS.body 
                                  }}
                                >
                                  {getWeekdayName(template.weekday)}
                                </span>
                              </div>
                              <button
                                onClick={() => toggleWeekdayExpanded(template.weekday)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: 2,
                                  color: COLORS.ACCENT_GRAY,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {expandedWeekdays.includes(template.weekday) ? (
                                  <ChevronUp size={14} aria-hidden="true" />
                                ) : (
                                  <ChevronDown size={14} aria-hidden="true" />
                                )}
                              </button>
                            </div>
                            
                            {/* Template Settings */}
                            {template.enabled && (
                              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                <div style={{ flex: 1 }}>
                                  <label style={{ fontSize: 10, fontWeight: 500, marginBottom: 2, display: 'block', ...FONTS.body }}>
                                    Default Max Units
                                  </label>
                                  <input
                                    type="text"
                                    value={template.default_max_total !== undefined ? template.default_max_total : 0}
                                    onChange={(e) => {
                                      const inputValue = e.target.value;
                                      if (inputValue === '' || inputValue === '0') {
                                        updateInventoryArray('template', template.weekday, 'default_max_total', 0);
                                      } else {
                                        const numValue = parseInt(inputValue);
                                        if (!isNaN(numValue) && numValue >= 0) {
                                          updateInventoryArray('template', template.weekday, 'default_max_total', numValue);
                                        }
                                      }
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '4px 6px',
                                      border: '1px solid #E0E0E0',
                                      borderRadius: 4,
                                      fontSize: 10,
                                      ...FONTS.body
                                    }}
                                  />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <label style={{ fontSize: 10, fontWeight: 500, marginBottom: 2, display: 'block', ...FONTS.body }}>
                                    Start Time
                                  </label>
                                  <input
                                    type="time"
                                    value={template.start_time_local || '08:00'}
                                    onChange={(e) => updateInventoryArray('template', template.weekday, 'start_time_local', e.target.value)}
                                    style={{
                                      width: '100%',
                                      padding: '4px 6px',
                                      border: '1px solid #E0E0E0',
                                      borderRadius: 4,
                                      fontSize: 10,
                                      ...FONTS.body
                                    }}
                                  />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <label style={{ fontSize: 10, fontWeight: 500, marginBottom: 2, display: 'block', ...FONTS.body }}>
                                    End Time
                                  </label>
                                  <input
                                    type="time"
                                    value={template.end_time_local || '18:00'}
                                    onChange={(e) => updateInventoryArray('template', template.weekday, 'end_time_local', e.target.value)}
                                    style={{
                                      width: '100%',
                                      padding: '4px 6px',
                                      border: '1px solid #E0E0E0',
                                      borderRadius: 4,
                                      fontSize: 10,
                                      ...FONTS.body
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Individual Dates */}
                            {template.enabled && expandedWeekdays.includes(template.weekday) && (
                              <div style={{ 
                                borderTop: '1px solid #E0E0E0', 
                                paddingTop: 8,
                                marginTop: 8
                              }}>
                                                                 <div style={{ 
                                   display: 'flex', 
                                   justifyContent: 'space-between', 
                                   alignItems: 'center',
                                   marginBottom: 6 
                                 }}>
                                   <span style={{ fontSize: 10, fontWeight: 600, color: COLORS.ACCENT_GRAY, ...FONTS.body }}>
                                     Individual Dates (Next 4 weeks)
                                   </span>
                                 </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {weekdayDates.map((dateInfo, dateIndex) => {
                                    const scheduledDate = getScheduledDate(dateInfo.date);
                                     return (
                                      <div key={dateInfo.date} style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 8,
                                        padding: '4px 6px',
                                        background: scheduledDate ? COLORS.NEUTRAL_BG : 'transparent',
                                        borderRadius: 4,
                                        border: scheduledDate ? '1px solid #E0E0E0' : 'none'
                                      }}>
                                        <span style={{ 
                                          fontSize: 10, 
                                          width: 80,
                                          color: COLORS.ACCENT_GRAY,
                                          ...FONTS.body 
                                        }}>
                                          {dateInfo.formatted}
                                        </span>
                                        
                                         <input
                                           key={`${dateInfo.date}-${template.default_max_total}`}
                                           type="number"
                                           min="0"
                                           placeholder="Units"

                                           value={(() => {
                                             // If template is disabled, show 0
                                             if (!template.enabled) {
                                               return 0;
                                             }
                                             
                                             // If there's a scheduled date, use its value
                                             if (scheduledDate && scheduledDate.max_total !== undefined) {
                                               return scheduledDate.max_total;
                                             }
                                             
                                             // Otherwise, use the template value for this weekday
                                             const templateValue = template.default_max_total !== undefined ? template.default_max_total : 0;
                                             
                                             return templateValue;
                                           })()}
                                           onChange={(e) => updateScheduledDate(dateInfo.date, parseInt(e.target.value) || 0)}
                                           disabled={!template.enabled}
                                           style={{
                                             width: 60,
                                             padding: '2px 4px',
                                             border: '1px solid #E0E0E0',
                                             borderRadius: 3,
                                             fontSize: 9,
                                             backgroundColor: !template.enabled ? '#F5F5F5' : 'white',
                                             color: !template.enabled ? '#999' : 'inherit',
                                             ...FONTS.body
                                           }}
                                         />
                                                                                 <span style={{ 
                                           fontSize: 9, 
                                           color: scheduledDate?.source === 'manual' ? COLORS.WARNING_ORANGE : COLORS.ACCENT_GRAY,
                                           ...FONTS.body 
                                         }}>
                                           {scheduledDate ? 
                                             `(${scheduledDate.ordered_total || 0} ordered${scheduledDate.source === 'manual' ? ', manual' : ''})` : 
                                             '(0 ordered)'
                                           }
                                         </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>



                  
               </div>



              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  style={{
                    flex: 1,
                    background: COLORS.NEUTRAL_BG,
                    color: COLORS.NEUTRAL_TEXT,
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    opacity: isSaving ? 0.6 : 1,
                    ...FONTS.body
                  }}
                  aria-label="Cancel editing"
                >
                  <X size={16} aria-hidden="true" />
                  Cancel
                </button>
                
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  style={{
                    flex: 1,
                    background: COLORS.PRIMARY_GRADIENT,
                    color: COLORS.NEUTRAL_WHITE,
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    opacity: isSaving ? 0.6 : 1,
                    ...FONTS.body
                  }}
                  aria-label="Save changes"
                >
                  <Save size={16} aria-hidden="true" />
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            /* View Mode */
            <div>
              {/* Product Details */}
              <div style={{ marginBottom: 16 }}>
                {product.description && (
                  <p style={{
                    fontSize: 12,
                    color: COLORS.ACCENT_GRAY,
                    margin: '0 0 8px 0',
                    lineHeight: 1.4,
                    ...FONTS.body
                  }}>
                    {product.description}
                  </p>
                )}
                
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  fontSize: 11,
                  color: COLORS.ACCENT_GRAY,
                  ...FONTS.body
                }}>
                  {product.listingType && (
                    <span style={{
                      background: COLORS.NEUTRAL_BG,
                      padding: '2px 6px',
                      borderRadius: 4
                    }}>
                      {getDisplayValue('listingType', product.listingType)}
                    </span>
                  )}
                  {product.cuisine && (
                    <span style={{
                      background: COLORS.NEUTRAL_BG,
                      padding: '2px 6px',
                      borderRadius: 4
                    }}>
                      {getDisplayValue('cuisine', product.cuisine)}
                    </span>
                  )}
                  {product.courseType && (
                    <span style={{
                      background: COLORS.NEUTRAL_BG,
                      padding: '2px 6px',
                      borderRadius: 4
                    }}>
                      {getDisplayValue('courseType', product.courseType)}
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: 12
              }}>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  style={{
                    flex: 1,
                    background: COLORS.NEUTRAL_WHITE,
                    color: COLORS.ERROR_RED,
                    border: `1px solid ${COLORS.ERROR_RED}`,
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    opacity: isDeleting ? 0.6 : 1,
                    ...FONTS.body
                  }}
                  aria-label={`Delete ${product.name}`}
                >
                  <Trash size={16} aria-hidden="true" style={{ color: COLORS.ERROR_RED }} />
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
                
                <button
                  onClick={handleEdit}
                  disabled={isDeleting}
                  style={{
                    flex: 1,
                    background: COLORS.NEUTRAL_BG,
                    color: COLORS.NEUTRAL_TEXT,
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    opacity: isDeleting ? 0.6 : 1,
                    ...FONTS.body
                  }}
                  aria-label={`Edit ${product.name}`}
                >
                  <Edit size={16} aria-hidden="true" />
                  Edit
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Add CSS for animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
document.head.appendChild(style);
